<template>
  <div class="page exam-page">
    <xh-header />

    <div class="container-width" v-if="course">
      <div class="ex-bread">
        <span class="mouse" @click="back">
          <i class="el-icon-arrow-left"></i> 返回课程
        </span>
      </div>

      <!-- 答题阶段 -->
      <div v-if="!finished">
        <div class="ex-head">
          <div class="ex-title">
            <i class="el-icon-document-checked"></i>
            {{ course.courseName }} · 课程考试
          </div>
          <div class="ex-meta">
            <span>题目数 <b>{{ questions.length }}</b></span>
            <span>已答 <b :class="{ 'green': answeredCount === questions.length }">{{ answeredCount }}</b> / {{ questions.length }}</span>
            <span>用时 <b>{{ formatTime(elapsed) }}</b></span>
          </div>
        </div>

        <div class="rule-tip">
          <i class="el-icon-medal"></i>
          及格规则：{{ passRuleText }}
        </div>

        <div v-if="!questions.length && loaded" class="empty-block">
          本课程暂未配置考试题目 <el-button size="small" @click="back">返回</el-button>
        </div>

        <div class="q-list">
          <div class="q-item" v-for="(q, idx) in questions" :key="q.id">
            <div class="q-head">
              <span class="q-no">{{ idx + 1 }}.</span>
              <span class="q-text">{{ q.question }}</span>
              <el-tag size="mini" :type="q.questionType === 2 ? 'warning' : 'info'">
                {{ q.questionType === 2 ? '多选' : '单选' }}
              </el-tag>
            </div>
            <div class="q-options">
              <template v-if="q.questionType === 2">
                <el-checkbox-group v-model="multiAnswers[q.id]">
                  <el-checkbox
                    v-for="opt in q.options"
                    :key="opt.k"
                    :label="opt.k"
                    class="opt"
                  >
                    <span class="opt-key">{{ opt.k }}.</span>
                    <span class="opt-text">{{ opt.v }}</span>
                  </el-checkbox>
                </el-checkbox-group>
              </template>
              <template v-else>
                <el-radio-group v-model="singleAnswers[q.id]">
                  <el-radio
                    v-for="opt in q.options"
                    :key="opt.k"
                    :label="opt.k"
                    class="opt"
                  >
                    <span class="opt-key">{{ opt.k }}.</span>
                    <span class="opt-text">{{ opt.v }}</span>
                  </el-radio>
                </el-radio-group>
              </template>
            </div>
          </div>
        </div>

        <div class="submit-area" v-if="questions.length">
          <el-button type="primary" size="medium" :loading="submitting" @click="submit">
            交卷
          </el-button>
          <span class="sub-tip">请确认所有题目答案后再提交</span>
        </div>
      </div>

      <!-- 结果阶段 -->
      <div v-else class="result-card">
        <div class="r-icon" :class="result.passFlag === 1 ? 'ok' : 'fail'">
          <i :class="result.passFlag === 1 ? 'el-icon-success' : 'el-icon-warning'"></i>
        </div>
        <div class="r-title">
          {{ result.passFlag === 1 ? '恭喜，考试通过' : '很遗憾，本次未通过' }}
        </div>
        <div class="r-stat">
          <div>
            <div class="rs-num">{{ result.score }}</div>
            <div class="rs-lab">得分</div>
          </div>
          <div>
            <div class="rs-num">{{ result.totalNum }}</div>
            <div class="rs-lab">题目数</div>
          </div>
          <div>
            <div class="rs-num">{{ formatTime(result.duration) }}</div>
            <div class="rs-lab">用时</div>
          </div>
        </div>
        <div class="r-rule">
          {{ passRuleText }}
          <template v-if="result.passFlag !== 1">
            <br/><span class="r-tip">提示：本平台不公布每题对错与正确答案，建议重看课程视频后再次参加考试。</span>
          </template>
        </div>
        <div class="r-actions">
          <el-button type="primary" @click="goRecord">查看作答记录</el-button>
          <el-button @click="retry" v-if="result.passFlag !== 1">重新答题</el-button>
          <el-button @click="back">返回课程</el-button>
        </div>
      </div>
    </div>

    <xh-footer />
  </div>
</template>

<script>
import XhHeader from '../../../reveals/xh-header.vue'
import XhFooter from '../../../reveals/xh-footer.vue'
import {
  getStudyCourseDetail,
  getStudyExamQuestions,
  submitStudyExam
} from '@/service/apis/study'

export default {
  name: 'StudyExam',
  components: { XhHeader, XhFooter },
  data () {
    return {
      courseId: null,
      account: null,
      course: null,
      questions: [],
      loaded: false,
      singleAnswers: {},  // {qid: 'A'}
      multiAnswers: {},   // {qid: ['A','B']}
      examStartTs: 0,
      elapsed: 0,
      timer: null,
      submitting: false,
      finished: false,
      result: null
    }
  },
  computed: {
    answeredCount () {
      let n = 0
      this.questions.forEach(q => {
        if (q.questionType === 2) {
          if ((this.multiAnswers[q.id] || []).length > 0) n++
        } else {
          if (this.singleAnswers[q.id]) n++
        }
      })
      return n
    },
    passRuleText () {
      if (this.course && this.course.passRightNum > 0) {
        return `答对题数 ≥ ${ this.course.passRightNum } 题视为通过`
      }
      return '得分 ≥ 60 分视为通过'
    }
  },
  created () {
    const cid = this.$route.query.courseId
    if (!cid) {
      this.$message.error('参数缺失')
      this.$router.replace('/userinfo')
      return
    }
    this.courseId = Number(cid)
    const acct = localStorage.getItem('ACCOUNT')
    if (!acct) {
      this.$router.replace('/login')
      return
    }
    this.account = JSON.parse(acct)
    this.examStartTs = Date.now()
    this.timer = setInterval(() => {
      this.elapsed = Math.floor((Date.now() - this.examStartTs) / 1000)
    }, 1000)
    this.loadAll()
  },
  beforeDestroy () {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    formatTime (sec) {
      sec = Math.floor(Number(sec) || 0)
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = sec % 60
      const pad = n => String(n).padStart(2, '0')
      return h > 0 ? `${ pad(h) }:${ pad(m) }:${ pad(s) }` : `${ pad(m) }:${ pad(s) }`
    },
    handle9994 (res) {
      if (res && res.errCode === 9994) {
        localStorage.clear()
        this.$router.replace('/login')
        return true
      }
      return false
    },
    loadAll () {
      getStudyCourseDetail.call(this, { id: this.courseId }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0) this.course = res.data
      })
      getStudyExamQuestions.call(this, {
        courseId: this.courseId,
        pageIndex: 1,
        pageSize: 1000
      }).then(res => {
        if (this.handle9994(res)) return
        if (res.errCode === 0 && res.data && Array.isArray(res.data.records)) {
          this.questions = res.data.records.map(q => ({
            ...q,
            options: this.buildOptions(q)
          }))
          // 初始化答案占位
          this.questions.forEach(q => {
            if (q.questionType === 2) {
              this.$set(this.multiAnswers, q.id, [])
            } else {
              this.$set(this.singleAnswers, q.id, '')
            }
          })
        }
        this.loaded = true
      })
    },
    buildOptions (q) {
      return ['A', 'B', 'C', 'D']
        .filter(k => q['option' + k])
        .map(k => ({ k, v: q['option' + k] }))
    },
    submit () {
      const total = this.questions.length
      const answered = this.answeredCount
      const unanswered = total - answered
      const doSubmit = () => {
        this.submitting = true
        const answers = this.questions.map(q => {
          let userAnswer = ''
          if (q.questionType === 2) {
            const arr = (this.multiAnswers[q.id] || []).slice().sort()
            userAnswer = arr.join('')
          } else {
            userAnswer = this.singleAnswers[q.id] || ''
          }
          return { questionId: q.id, userAnswer }
        })
        const examStartTime = this.formatDate(this.examStartTs, 'YYYY-mm-dd hh:ii:ss')
        submitStudyExam.call(this, {
          courseId: this.courseId,
          examStartTime,
          duration: this.elapsed,
          answers
        }).then(res => {
          this.submitting = false
          if (this.handle9994(res)) return
          if (res.errCode === 0 && res.data && res.data.record) {
            this.result = res.data.record
            this.finished = true
            if (this.timer) clearInterval(this.timer)
          }
        }).catch(() => {
          this.submitting = false
        })
      }
      if (unanswered > 0) {
        this.$confirm(`您还有 ${ unanswered } 道题未作答，确认交卷？`, '提示', {
          confirmButtonText: '确认交卷',
          cancelButtonText: '继续答题',
          type: 'warning'
        }).then(doSubmit).catch(() => {})
      } else {
        this.$confirm('答案提交后无法修改，确认交卷？', '提示', {
          confirmButtonText: '确认交卷',
          cancelButtonText: '再检查一下',
          type: 'info'
        }).then(doSubmit).catch(() => {})
      }
    },
    goRecord () {
      this.$router.push(`/study/exam-record?courseId=${ this.courseId }&id=${ this.result.id }`)
    },
    retry () {
      // 复位本页状态，开启新一轮
      this.singleAnswers = {}
      this.multiAnswers = {}
      this.questions.forEach(q => {
        if (q.questionType === 2) {
          this.$set(this.multiAnswers, q.id, [])
        } else {
          this.$set(this.singleAnswers, q.id, '')
        }
      })
      this.examStartTs = Date.now()
      this.elapsed = 0
      this.finished = false
      this.result = null
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.elapsed = Math.floor((Date.now() - this.examStartTs) / 1000)
      }, 1000)
      window.scrollTo(0, 0)
    },
    back () {
      this.$router.push('/study/course?id=' + this.courseId)
    }
  }
}
</script>

<style scoped>
.exam-page { background: #fff; }
.container-width { padding: 16px 0 60px; min-height: 60vh; }
.ex-bread {
  font-size: 13px;
  color: #888;
  padding: 6px 0 12px;
}
.ex-bread .mouse:hover { color: #4c2ac9; }
.ex-head {
  border: solid 1px #ece8ff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #faf8ff 0%, #fff 60%);
}
.ex-title {
  font-size: 18px;
  font-weight: 600;
  color: #2e2e2e;
}
.ex-title i { color: #4c2ac9; margin-right: 6px; }
.ex-meta {
  margin-top: 10px;
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 22px;
}
.ex-meta b { color: #4c2ac9; padding: 0 2px; }
.ex-meta b.green { color: #4caf50; }

.rule-tip {
  font-size: 13px;
  color: #b35c00;
  background: #fff7e6;
  padding: 8px 14px;
  border-radius: 4px;
  margin-bottom: 16px;
}
.rule-tip i { margin-right: 4px; }

.q-list {
  border: solid 1px #f0f0f0;
  border-radius: 8px;
  padding: 6px 0;
}
.q-item {
  padding: 18px 22px;
  border-bottom: solid 1px #f5f5f5;
}
.q-item:last-child { border-bottom: none; }
.q-head {
  font-size: 15px;
  color: #2e2e2e;
  line-height: 24px;
  margin-bottom: 14px;
}
.q-no { color: #4c2ac9; font-weight: 600; margin-right: 6px; }
.q-head .el-tag { margin-left: 8px; }
.q-options { padding-left: 8px; }
.q-options .opt {
  display: block;
  padding: 6px 0;
  white-space: normal;
  font-weight: normal;
  color: #555;
}
.q-options .opt-key {
  color: #4c2ac9;
  font-weight: 600;
  margin-right: 6px;
}
.q-options .opt-text { color: #555; }

.submit-area {
  text-align: center;
  margin: 28px 0 0;
}
.submit-area .sub-tip {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

.empty-block {
  text-align: center;
  color: #999;
  padding: 60px 20px;
  font-size: 14px;
}

/* 结果卡 */
.result-card {
  border: solid 1px #ece8ff;
  border-radius: 12px;
  padding: 36px 24px 30px;
  text-align: center;
  background: linear-gradient(180deg, #faf8ff 0%, #fff 80%);
}
.r-icon { font-size: 64px; }
.r-icon.ok { color: #4caf50; }
.r-icon.fail { color: #f0a020; }
.r-title {
  font-size: 22px;
  color: #2e2e2e;
  font-weight: 600;
  margin: 12px 0 24px;
}
.r-stat {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
}
.r-stat > div {
  flex: 0 1 110px;
  text-align: center;
  border-right: solid 1px #f0ebff;
  padding: 0 6px;
}
.r-stat > div:last-child { border-right: none; }
.rs-num {
  font-size: 24px;
  color: #4c2ac9;
  font-weight: 600;
  line-height: 32px;
}
.rs-num.green { color: #4caf50; }
.rs-num.red { color: #f56c6c; }
.rs-lab { font-size: 12px; color: #999; }
.r-rule {
  font-size: 13px;
  color: #888;
  margin-bottom: 22px;
}
.r-actions .el-button { margin: 0 4px; }

@media screen and (max-width: 800px) {
  .container-width { padding: 8px 16px 30px; }
  .ex-head { padding: 14px 14px; }
  .ex-title { font-size: 16px; }
  .ex-meta { font-size: 12px; gap: 14px; flex-wrap: wrap; }
  .q-item { padding: 14px 14px; }
  .q-head { font-size: 14px; }
  .r-title { font-size: 18px; }
  .r-stat { flex-wrap: wrap; }
  .r-stat > div {
    flex: 0 0 33.33%;
    border-right: none;
    padding: 6px 0;
  }
  .rs-num { font-size: 18px; }
  .r-actions .el-button { margin: 4px; }
}
</style>
